<template>
  <div class="p-6 dark:text-white" v-if="config">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="$router.push({ name: 'AlloyConfigs' })"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
          Back
        </button>
        Observability Config: {{ config.name }}
      </h1>
    </div>

    <!-- Status Badge -->
    <div class="mb-4">
      <span :class="[
        'px-3 py-1 text-sm rounded-full',
        config.pending ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
      ]">
        {{ config.pending ? 'Pending Approval' : 'Approved' }}
      </span>
      <span v-if="config.marked_for_deletion" class="ml-2 px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
        Marked for Deletion
      </span>
    </div>

    <!-- Config Info Card -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Configuration Details</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Linked Entity</label>
          <p class="font-medium">
            <span :class="[
              'px-2 py-1 text-xs rounded-full mr-2',
              config.linked_entity_type === 'probe' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
            ]">
              {{ config.linked_entity_type }}
            </span>
            {{ config.linked_entity_name }}
          </p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Created</label>
          <p class="font-medium">{{ formatDateTime(config.created_date) }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Last Modified</label>
          <p class="font-medium">{{ formatDateTime(config.last_modified) }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Organization</label>
          <p class="font-medium">{{ config.org_name || 'N/A' }}</p>
        </div>
      </div>
    </div>

    <!-- Tabs (unified glass-tab styling, mirrors ProbeDetails.vue) -->
    <div class="glass-card mb-2">
      <nav class="flex px-2 py-2 overflow-x-auto">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          :class="[
            'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium transition-colors duration-150 ease-in-out',
            activeTab === tab.key
              ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
              : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400',
          ]"
        >
          {{ tab.label }}
          <span class="ml-2 px-2 py-0.5 text-xs rounded-full border border-gray-300/60 dark:border-gray-600/60">
            {{ getJobCount(tab.key) }}
          </span>
        </button>
        <div v-if="user?.is_staff" class="ml-auto flex items-center pl-4 pr-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
          <span>Show job names</span>
          <div class="relative inline-block w-12 ml-2 align-middle select-none transition duration-200 ease-in flex-shrink-0">
            <input
              id="show-job-names"
              type="checkbox"
              v-model="showJobNames"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
            />
            <label for="show-job-names" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
      </nav>
    </div>

    <!-- Tab Content -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <!-- SNMP Jobs Tab -->
        <div v-if="activeTab === 'snmp' && (canViewSnmpJob || user?.is_staff)">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">SNMP Jobs</h3>
            <button
              v-if="canAddSnmpJob || user?.is_staff"
              @click="$router.push({ name: 'AddAlloySNMP', params: { configId: config.id }, query: { schemaName: currentSchema } })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add SNMP Job
            </button>
          </div>
          <JobTable
            :jobs="config.snmp_jobs || []"
            :columns="snmpColumns"
            :can-edit="canChangeSnmpJob || user?.is_staff"
            :can-delete="canDeleteSnmpJob || user?.is_staff"
            @delete="deleteJob('snmp', $event)"
            @edit="editJob('snmp', $event)"
          />
        </div>

        <!-- Blackbox Jobs Tab -->
        <div v-if="activeTab === 'blackbox' && (canViewBlackboxJob || user?.is_staff)">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Endpoint Test Jobs</h3>
            <button
              v-if="canAddBlackboxJob || user?.is_staff"
              @click="$router.push({ name: 'AddAlloyBlackbox', params: { configId: config.id }, query: { schemaName: currentSchema } })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add Endpoint Test
            </button>
          </div>
          <JobTable
            :jobs="config.blackbox_jobs || []"
            :columns="blackboxColumns"
            :can-edit="canChangeBlackboxJob || user?.is_staff"
            :can-delete="canDeleteBlackboxJob || user?.is_staff"
            @delete="deleteJob('blackbox', $event)"
            @edit="editJob('blackbox', $event)"
          />
        </div>

        <!-- Fortigate Jobs Tab -->
        <div v-if="activeTab === 'fortigate' && (canViewFortigateJob || user?.is_staff)">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">API Exporter Jobs</h3>
            <button
              v-if="canAddFortigateJob || user?.is_staff"
              @click="$router.push({ name: 'AddAlloyFortigate', params: { configId: config.id }, query: { schemaName: currentSchema } })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add API Job
            </button>
          </div>
          <JobTable
            :jobs="config.fortigate_jobs || []"
            :columns="fortigateColumns"
            :can-edit="canChangeFortigateJob || user?.is_staff"
            :can-delete="canDeleteFortigateJob || user?.is_staff"
            @delete="deleteJob('fortigate', $event)"
            @edit="editJob('fortigate', $event)"
          />
        </div>

        <!-- Darkstat Jobs Tab -->
        <div v-if="activeTab === 'darkstat' && (canViewDarkstatJob || user?.is_staff)">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Top Talkers Jobs</h3>
            <button
              v-if="canAddDarkstatJob || user?.is_staff"
              @click="$router.push({ name: 'AddAlloyDarkstat', params: { configId: config.id }, query: { schemaName: currentSchema } })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add Top Talkers Job
            </button>
          </div>
          <JobTable
            :jobs="config.darkstat_jobs || []"
            :columns="darkstatColumns"
            :can-edit="canChangeDarkstatJob || user?.is_staff"
            :can-delete="canDeleteDarkstatJob || user?.is_staff"
            @delete="deleteJob('darkstat', $event)"
            @edit="editJob('darkstat', $event)"
          />
        </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingJob" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
        <p class="mb-4">Are you sure you want to delete this {{ deletingJob.type }} job?</p>
        <p class="font-medium text-gray-600 dark:text-gray-300 mb-6">{{ deletingJob.job.job_description || 'this job' }}</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="deletingJob = null"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            @click="confirmDeleteJob"
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div v-else-if="!error" class="p-6">
    <p>Loading...</p>
  </div>

  <!-- Error State -->
  <PageError v-if="error" :error="error" :errorCode="errorCode" />
</template>

<script>
import {
  getAlloyConfigById,
  deleteAlloySNMP,
  deleteAlloyBlackbox,
  deleteAlloyFortigate,
  deleteAlloyDarkstat,
} from "@/services/alloyService";
import PageError from "@/components/PageError.vue";
import JobTable from "@/components/JobTable.vue";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AlloyDetails",
  components: {
    PageError,
    JobTable,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      config: null,
      error: "",
      errorCode: "",
      activeTab: this.$route.query.tab || "snmp",
      deletingJob: null,
      showJobNames: false,
      canApprove: false,
      canAddJobs: false,
      canAddSnmpJob: false,
      canAddBlackboxJob: false,
      canAddFortigateJob: false,
      canAddDarkstatJob: false,
      canChangeSnmpJob: false,
      canChangeBlackboxJob: false,
      canChangeFortigateJob: false,
      canChangeDarkstatJob: false,
      canDeleteSnmpJob: false,
      canDeleteBlackboxJob: false,
      canDeleteFortigateJob: false,
      canDeleteDarkstatJob: false,
      canViewSnmpJob: false,
      canViewBlackboxJob: false,
      canViewFortigateJob: false,
      canViewDarkstatJob: false,
      allTabs: [
        { key: "snmp", label: "SNMP" },
        { key: "blackbox", label: "Endpoint Tests" },
        { key: "fortigate", label: "API" },
        { key: "darkstat", label: "Top Talkers" },
      ],
      snmpColumnsBase: [
        { key: "version", label: "Version" },
        { key: "module", label: "Module" },
        { key: "targets", label: "Targets", formatter: (v) => v?.length || 0 },
        { key: "scrape_interval", label: "Interval" },
      ],
      blackboxColumnsBase: [
        { key: "module", label: "Module", formatter: (v) => ({
            http_2xx: "HTTP Response Times",
            tcp_connect: "3WHS Response Times",
            icmp: "ICMP Response Times",
            dns: "DNS Response Times",
          }[v] || v) },
        { key: "targets", label: "Targets", formatter: (v) => v?.length || 0 },
        { key: "scrape_interval", label: "Interval" },
      ],
      fortigateColumnsBase: [
        { key: "target", label: "Target" },
        { key: "profile", label: "Profile" },
        { key: "scrape_interval", label: "Interval" },
      ],
      darkstatColumnsBase: [
        { key: "interface", label: "Interface" },
        { key: "local_network", label: "Local Network" },
        { key: "scrape_interval", label: "Interval" },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
    tabs() {
      const visibility = {
        snmp: this.canViewSnmpJob,
        blackbox: this.canViewBlackboxJob,
        fortigate: this.canViewFortigateJob,
        darkstat: this.canViewDarkstatJob,
      };
      return this.allTabs.filter(t => visibility[t.key] || this.user?.is_staff);
    },
    snmpColumns() {
      return this.withNameColumns(this.snmpColumnsBase);
    },
    blackboxColumns() {
      return this.withNameColumns(this.blackboxColumnsBase);
    },
    fortigateColumns() {
      return this.withNameColumns(this.fortigateColumnsBase);
    },
    darkstatColumns() {
      return this.withNameColumns(this.darkstatColumnsBase);
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.fetchConfig();
    await this.checkPermissions();
  },
  methods: {
    withNameColumns(baseColumns) {
      const descriptionColumn = { key: "job_description", label: "Description", emptyText: "No description" };
      const jobNameColumn = { key: "job_name", label: "Job Name", formatter: (jobName) => (jobName || "").replace(/^Alloy-/, "") };
      return this.user?.is_staff && this.showJobNames
        ? [descriptionColumn, jobNameColumn, ...baseColumns]
        : [descriptionColumn, ...baseColumns];
    },
    async checkPermissions() {
      if (this.user) {
        this.canApprove = await useStore.hasPermission("Can change Alloy Config");
        this.canAddJobs = await useStore.hasPermission("Can add Alloy Config");
        this.canAddSnmpJob = await useStore.hasPermission("Can add Alloy SNMP Job");
        this.canAddBlackboxJob = await useStore.hasPermission("Can add Alloy Blackbox Job");
        this.canAddFortigateJob = await useStore.hasPermission("Can add Alloy Fortigate Exporter Job");
        this.canAddDarkstatJob = await useStore.hasPermission("Can add Alloy Darkstat Job");
        this.canChangeSnmpJob = await useStore.hasPermission("Can change Alloy SNMP Job");
        this.canChangeBlackboxJob = await useStore.hasPermission("Can change Alloy Blackbox Job");
        this.canChangeFortigateJob = await useStore.hasPermission("Can change Alloy Fortigate Exporter Job");
        this.canChangeDarkstatJob = await useStore.hasPermission("Can change Alloy Darkstat Job");
        this.canDeleteSnmpJob = await useStore.hasPermission("Can delete Alloy SNMP Job");
        this.canDeleteBlackboxJob = await useStore.hasPermission("Can delete Alloy Blackbox Job");
        this.canDeleteFortigateJob = await useStore.hasPermission("Can delete Alloy Fortigate Exporter Job");
        this.canDeleteDarkstatJob = await useStore.hasPermission("Can delete Alloy Darkstat Job");
        this.canViewSnmpJob = await useStore.hasPermission("Can view Alloy SNMP Job");
        this.canViewBlackboxJob = await useStore.hasPermission("Can view Alloy Blackbox Job");
        this.canViewFortigateJob = await useStore.hasPermission("Can view Alloy Fortigate Exporter Job");
        this.canViewDarkstatJob = await useStore.hasPermission("Can view Alloy Darkstat Job");
        // If the active tab was hidden by perms, fall back to the first visible one.
        if (this.tabs.length && !this.tabs.some(t => t.key === this.activeTab)) {
          this.activeTab = this.tabs[0].key;
        }
      }
    },
    async fetchConfig() {
      try {
        const configId = this.$route.params.id;
        this.config = await getAlloyConfigById(configId, this.currentSchema);
      } catch (error) {
        console.error("Error fetching alloy config:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    getJobCount(tabKey) {
      if (!this.config) return 0;
      const countMap = {
        snmp: this.config.snmp_jobs?.length || 0,
        blackbox: this.config.blackbox_jobs?.length || 0,
        fortigate: this.config.fortigate_jobs?.length || 0,
        darkstat: this.config.darkstat_jobs?.length || 0,
      };
      return countMap[tabKey];
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    deleteJob(type, job) {
      this.deletingJob = { type, job };
    },
    editJob(type, job) {
      const routeMap = {
        snmp: "EditAlloySNMP",
        blackbox: "EditAlloyBlackbox",
        fortigate: "EditAlloyFortigate",
        darkstat: "EditAlloyDarkstat",
      };
      this.$router.push({
        name: routeMap[type],
        params: { configId: this.config.id, jobId: job.id },
        query: { schemaName: this.currentSchema },
      });
    },
    async confirmDeleteJob() {
      if (!this.deletingJob) return;

      const { type, job } = this.deletingJob;
      const deleteMap = {
        snmp: deleteAlloySNMP,
        blackbox: deleteAlloyBlackbox,
        fortigate: deleteAlloyFortigate,
        darkstat: deleteAlloyDarkstat,
      };

      try {
        await deleteMap[type](job.id, this.currentSchema);
        await this.fetchConfig();
        this.deletingJob = null;
      } catch (error) {
        console.error("Error deleting job:", error);
        alert("Failed to delete job");
      }
    },
  },
};
</script>
